import { useState } from "react";
import {
  useAuth,
  useAuthMethods,
  signInWithPassword,
  signUpWithPassword,
} from "../lib/auth-client";
import { I } from "../components/Icons";
import { MSLogo } from "../components/MicrosoftSheet";
import { nymblLogoWhite } from "../assets";

export function LoginScreen() {
  const { signIn } = useAuth();
  const { methods } = useAuthMethods();
  const [redirecting, setRedirecting] = useState(false);

  async function handleSignIn() {
    setRedirecting(true);
    try {
      await signIn();
    } catch (err) {
      console.error("Microsoft sign-in failed", err);
      setRedirecting(false);
    }
  }

  const showEntra = methods?.entra ?? true;
  const showLocal = methods?.localAuth ?? false;

  return (
    <div className="gf-login">
      <div className="gf-login__left">
        <div className="gf-login__brand">
          <img src={nymblLogoWhite} alt="Nymbl" />
          <div className="gf-login__brand-divider"></div>
          <div className="gf-login__brand-app">Git Fit</div>
        </div>

        <div className="gf-login__hero">
          <div className="gf-login__eyebrow">Internal Training · PM Track</div>
          <h1 className="gf-login__title">Learn git, branch by branch.</h1>
          <p className="gf-login__sub">
            A guided, visual course that takes product managers from zero to
            confidently reviewing pull requests in an afternoon.
          </p>

          <div className="gf-login__bullets">
            <div className="gf-login__bullet">
              <div className="gf-login__bullet-icon">
                <I.GitCommit size={14} />
              </div>
              <div>Six short courses, built to be taught live by an instructor.</div>
            </div>
            <div className="gf-login__bullet">
              <div className="gf-login__bullet-icon">
                <I.Sparkles size={14} />
              </div>
              <div>Interactive git graph — actually commit, branch, and merge.</div>
            </div>
            <div className="gf-login__bullet">
              <div className="gf-login__bullet-icon">
                <I.Eye size={14} />
              </div>
              <div>Real GitHub UI mocks for the PR review workflow.</div>
            </div>
          </div>
        </div>

        <div className="gf-login__footer">
          <span>© 2026 Nymbl LLC</span>
          <span>·</span>
          <span>Quincy, MA</span>
        </div>
      </div>

      <div className="gf-login__right">
        <div className="gf-login__card">
          <h2>Sign in to Git&nbsp;Fit</h2>
          <p>
            {showEntra
              ? "Single sign-on through your Nymbl Microsoft account."
              : "Sign in with your email and password."}
          </p>

          {showEntra && (
            <button
              className="gf-login__sso"
              onClick={handleSignIn}
              disabled={redirecting}
            >
              {redirecting ? (
                <>
                  <span
                    className="gf-ms__spinner"
                    style={{ width: 16, height: 16, borderWidth: 2 }}
                  ></span>
                  <span>Redirecting to Microsoft…</span>
                </>
              ) : (
                <>
                  <MSLogo />
                  <span>Sign in with Microsoft</span>
                </>
              )}
            </button>
          )}

          {showEntra && !showLocal && (
            <div className="gf-login__divider">Secured by Entra ID</div>
          )}

          {showEntra && showLocal && (
            <div className="gf-login__divider">Or with email</div>
          )}

          {showLocal && <PasswordForm />}

          <div className="gf-login__hint">
            <b>Heads up:</b> this is a learning environment. Nothing you do here
            touches a real repository — feel free to break things.
          </div>

          <div className="gf-login__legal">
            By signing in you agree to Nymbl's{" "}
            <a href="#">Acceptable Use Policy</a>.
          </div>
        </div>
      </div>
    </div>
  );
}

function PasswordForm() {
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const err =
      mode === "signup"
        ? await signUpWithPassword(name.trim(), email.trim(), password)
        : await signInWithPassword(email.trim(), password);
    if (err) {
      setError(err);
      setBusy(false);
    }
    // On success the session updates and App routes to /dashboard; keep busy.
  }

  return (
    <form className="gf-login__form" onSubmit={handleSubmit}>
      {mode === "signup" && (
        <label className="gf-login__field">
          <span>Name</span>
          <input
            className="gf-login__input"
            type="text"
            autoComplete="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Erin Walsh"
          />
        </label>
      )}
      <label className="gf-login__field">
        <span>Email</span>
        <input
          className="gf-login__input"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@nymbl.app"
        />
      </label>
      <label className="gf-login__field">
        <span>Password</span>
        <input
          className="gf-login__input"
          type="password"
          autoComplete={mode === "signup" ? "new-password" : "current-password"}
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={mode === "signup" ? "At least 8 characters" : "••••••••"}
        />
      </label>

      {error && <div className="gf-login__error">{error}</div>}

      <button className="gf-btn gf-btn--gradient gf-login__submit" type="submit" disabled={busy}>
        {busy
          ? "Working…"
          : mode === "signup"
            ? "Create account"
            : "Sign in"}
      </button>

      <button
        type="button"
        className="gf-login__toggle"
        onClick={() => {
          setMode((m) => (m === "signin" ? "signup" : "signin"));
          setError(null);
        }}
      >
        {mode === "signin"
          ? "Need a test account? Create one"
          : "Already have an account? Sign in"}
      </button>
    </form>
  );
}
